import { Paths } from '@metorial/frontend-config';
import type { SubNavGroup, SubNavLink, SubNavSpec } from '@metorial/layout';
import {
  RiAlarmWarningLine,
  RiApps2Line,
  RiAppsLine,
  RiBarChartBoxLine,
  RiBellLine,
  RiBuilding2Line,
  RiCheckboxCircleLine,
  RiCompassDiscoverLine,
  RiDashboardLine,
  RiEqualizerLine,
  RiErrorWarningLine,
  RiEyeLine,
  RiFileCopyLine,
  RiFileList3Line,
  RiFileSettingsLine,
  RiFileShieldLine,
  RiFingerprintLine,
  RiFlashlightLine,
  RiFolder3Line,
  RiGitBranchLine,
  RiGitRepositoryLine,
  RiGithubLine,
  RiGlobalLine,
  RiGroupLine,
  RiHistoryLine,
  RiHome6Line,
  RiInboxLine,
  RiKey2Line,
  RiKeyLine,
  RiLayoutGridLine,
  RiLinksLine,
  RiLockLine,
  RiMagicLine,
  RiMailSendLine,
  RiPaletteLine,
  RiPlugLine,
  RiProfileLine,
  RiRadarLine,
  RiRobot2Line,
  RiRobotLine,
  RiRocketLine,
  RiServerLine,
  RiSettings2Line,
  RiSettings3Line,
  RiShareForwardLine,
  RiShieldCheckLine,
  RiShieldCrossLine,
  RiShieldKeyholeLine,
  RiShieldLine,
  RiShieldUserLine,
  RiSparkling2Line,
  RiStackLine,
  RiStarLine,
  RiStore2Line,
  RiTeamLine,
  RiTerminalLine,
  RiTicket2Line,
  RiTimeLine,
  RiToolsLine,
  RiUser3Line,
  RiUserLine,
  RiUserSettingsLine
} from '@remixicon/react';
import React from 'react';
import type { ShellScope } from './scope';

// The cloud's sections and their sub-navs (cloud-spec §0, §2, §4, §5, §6, §7, §8).
// Each builder returns plain data; `isActive` decides highlighting from the path.

export type ShellSection =
  | 'home'
  | 'integrations'
  | 'skills'
  | 'workforce'
  | 'developer'
  | 'explorer'
  | 'document'
  | 'settings'
  | 'account';

let integrationsRoots = [
  'integrations',
  'providers',
  'provider',
  'external-providers',
  'custom-providers',
  'custom-provider',
  'integration',
  'integration-instance',
  'magic-mcp',
  'public-mcp',
  'provider-sessions',
  'sessions',
  'session-connections',
  'tool-calls',
  'provider-errors',
  'provider-error',
  'provider-auth-events',
  'provider-auth-errors',
  'provider-auth-error',
  'provider-auth-event',
  'provider-session',
  'callback',
  'webhook-registration',
  'incoming-webhook',
  'event-destinations',
  'events',
  'event-destination',
  'event',
  'event-delivery',
  'chat-explorer',
  'chat-connections',
  'chat-connection',
  'chat-instance',
  'chat',
  'chat-event'
];

let skillsRoots = [
  'skills',
  'skill',
  'skill-template',
  'skill-group',
  'skill-marketplace',
  'skill-plugin'
];
let workforceRoots = ['workforce', 'portal'];
let developerRoots = ['developer', 'configurations', 'network', 'sandbox'];

/** Path segments after `/i/:instanceId/`, e.g. ['provider', 'linear', 'versions']. */
export let getProductSegments = (pathname: string) => {
  let parts = pathname.split('/').filter(Boolean);
  if (parts[0] != 'i') return [];
  return parts.slice(2);
};

export let getSection = (pathname: string): ShellSection => {
  let parts = pathname.split('/').filter(Boolean);
  if (parts[0] == 'o') return 'settings';
  if (parts[0] == 'account') return 'account';

  let [root] = getProductSegments(pathname);
  if (!root || root == 'home') return 'home';
  if (root == 'explorer') return 'explorer';
  if (root == 'doc' || root == 'skill-item-document') return 'document';
  if (integrationsRoots.includes(root)) return 'integrations';
  if (skillsRoots.includes(root)) return 'skills';
  if (workforceRoots.includes(root)) return 'workforce';
  if (developerRoots.includes(root)) return 'developer';
  return 'home';
};

// Path matching ---------------------------------------------------------------

let normalize = (path: string) => path.replace(/[?#].*$/, '').replace(/\/+$/, '') || '/';

/** Exact match, or a child path when `prefix` is set. */
let on = (pathname: string, to: string, opts?: { prefix?: boolean }) => {
  let p = normalize(pathname);
  let t = normalize(to);
  if (t == '#') return false;
  return p == t || (!!opts?.prefix && p.startsWith(`${t}/`));
};

/** Active when the product path starts with one of these segment lists. */
let productOn = (pathname: string, ...prefixes: string[][]) => {
  let segments = getProductSegments(pathname);
  return prefixes.some(prefix => prefix.every((s, i) => segments[i] == s));
};

type P = [ShellScope['organization'], ShellScope['project'], ShellScope['instance']];

let params = (scope: ShellScope): P => [scope.organization, scope.project, scope.instance];

// Integrations (§2) ----------------------------------------------------------

export let integrationsSubNav = (scope: ShellScope, pathname: string): SubNavSpec => {
  let p = params(scope);
  let pon = (...prefixes: string[][]) => productOn(pathname, ...prefixes);

  return {
    key: 'integrations',
    groups: [
      {
        items: [
          {
            label: 'Overview',
            icon: <RiDashboardLine />,
            to: Paths.instance.integrationsOverview(...p),
            active: pon(['integrations', 'overview'])
          },
          {
            label: 'Explorer',
            icon: <RiCompassDiscoverLine />,
            to: Paths.instance.explorer(...p),
            active: pon(['explorer'])
          }
        ]
      },
      {
        label: 'Catalog',
        items: [
          {
            label: 'Providers',
            icon: <RiApps2Line />,
            to: Paths.instance.providers(...p),
            active: pon(['providers'], ['provider'])
          },
          {
            label: 'Custom Providers',
            icon: <RiServerLine />,
            to: Paths.instance.externalProviders(...p),
            active: pon(['external-providers'], ['custom-providers'], ['custom-provider']),
            // Cloud: "Custom Providers" expands to its lists. BlackOS links remote MCP
            // servers by URL only (no Custom MCP Server creation).
            children: [
              {
                label: 'Remote MCP Servers',
                icon: <RiGlobalLine />,
                to: Paths.instance.externalProviders(...p),
                active: pon(['external-providers'])
              }
            ]
          }
        ]
      },
      {
        label: 'Integrations',
        items: [
          {
            label: 'Integrations',
            icon: <RiPlugLine />,
            to: Paths.instance.integrations(...p),
            active:
              (pon(['integrations']) && !pon(['integrations', 'overview'])) ||
              pon(['integration'], ['integration-instance'], ['callback'])
          },
          {
            label: 'Magic MCP',
            icon: <RiMagicLine />,
            to: Paths.instance.magicMcp.servers(...p),
            active: pon(['magic-mcp'])
          }
        ]
      },
      {
        label: 'Connection Logs',
        items: [
          {
            label: 'Sessions',
            icon: <RiHistoryLine />,
            to: Paths.instance.providerSessions(...p),
            active: pon(
              ['provider-sessions'],
              ['sessions'],
              ['session-connections'],
              ['provider-session']
            )
          },
          {
            label: 'Tool Calls',
            icon: <RiToolsLine />,
            to: Paths.instance.toolCalls(...p),
            active: pon(['tool-calls'])
          },
          {
            label: 'Tool Errors',
            icon: <RiErrorWarningLine />,
            to: Paths.instance.providerErrors(...p),
            active: pon(['provider-errors'], ['provider-error'])
          }
        ]
      },
      {
        label: 'Auth Logs',
        items: [
          {
            label: 'Authentications',
            icon: <RiShieldUserLine />,
            to: Paths.instance.providerAuthEvents(...p),
            active: pon(['provider-auth-events'], ['provider-auth-event'])
          },
          {
            label: 'Auth Errors',
            icon: <RiShieldCrossLine />,
            to: Paths.instance.providerAuthErrors(...p),
            active: pon(['provider-auth-errors'], ['provider-auth-error'])
          }
        ]
      }
    ]
  };
};

// Skills (§4) ----------------------------------------------------------------

export let skillsSubNav = (scope: ShellScope, pathname: string): SubNavSpec => {
  let p = params(scope);
  let segments = getProductSegments(pathname);
  let pon = (...prefixes: string[][]) => productOn(pathname, ...prefixes);

  return {
    key: 'skills',
    groups: [
      {
        items: [
          {
            label: 'Skills',
            icon: <RiSparkling2Line />,
            to: Paths.instance.skills(...p),
            active: (segments[0] == 'skills' && segments.length == 1) || pon(['skill'])
          },
          {
            label: 'Marketplaces',
            icon: <RiStore2Line />,
            to: Paths.instance.skillMarketplaces(...p),
            // Plugins open from a marketplace, so they keep Marketplaces highlighted.
            active: pon(['skills', 'marketplaces'], ['skill-marketplace'], ['skill-plugin'])
          },
          {
            label: 'Templates',
            icon: <RiFileCopyLine />,
            to: Paths.instance.skillTemplates(...p),
            active: pon(['skills', 'templates'], ['skill-template'])
          },
          {
            label: 'Groups',
            icon: <RiStackLine />,
            to: Paths.instance.skillGroups(...p),
            active: pon(['skills', 'groups'], ['skill-group'])
          }
        ]
      }
    ]
  };
};

// Workforce (§5) -------------------------------------------------------------

export let workforceSubNav = (
  scope: ShellScope,
  pathname: string,
  portalId: string | null | undefined
): SubNavSpec | null => {
  if (!portalId) return null;

  let p = params(scope);
  let w = Paths.instance.workforcePortal;
  let segments = getProductSegments(pathname);
  // segments: ['portal', portalId, ...rest]
  let rest = segments[0] == 'portal' ? segments.slice(2) : [];
  let at = (...prefix: string[]) => prefix.every((s, i) => rest[i] == s);

  return {
    key: 'workforce',
    groups: [
      {
        items: [
          {
            label: 'Overview',
            icon: <RiDashboardLine />,
            to: w.overview(...p, portalId),
            active: rest.length == 0
          }
        ]
      },
      {
        label: 'Access',
        items: [
          {
            label: 'Groups',
            icon: <RiTeamLine />,
            to: w.groups(...p, portalId),
            active: at('groups') || at('group')
          },
          {
            label: 'Skills',
            icon: <RiSparkling2Line />,
            to: w.skills(...p, portalId),
            active: at('resources', 'skills')
          },
          {
            label: 'Integrations',
            icon: <RiPlugLine />,
            to: w.integrations(...p, portalId),
            active: at('resources', 'integrations') || at('access-listing')
          },
          {
            label: 'Access Requests',
            icon: <RiInboxLine />,
            to: w.accessRequests(...p, portalId),
            active: at('access-requests')
          }
        ]
      },
      {
        label: 'Identity',
        items: [
          {
            label: 'Accounts',
            icon: <RiUserLine />,
            to: w.accounts(...p, portalId),
            active: (at('accounts') && !at('accounts', 'invites')) || at('account'),
            children: [
              {
                label: 'Invites',
                icon: <RiMailSendLine />,
                to: w.invites(...p, portalId),
                active: at('accounts', 'invites')
              }
            ]
          },
          {
            label: 'Agents',
            icon: <RiRobot2Line />,
            to: w.agents(...p, portalId),
            active: at('agents')
          },
          {
            label: 'Identities',
            icon: <RiFingerprintLine />,
            to: w.identities(...p, portalId),
            active: at('identities') || at('identity'),
            children: [
              {
                label: 'Actors',
                icon: <RiUser3Line />,
                to: w.actors(...p, portalId),
                active: at('actors')
              }
            ]
          }
        ]
      },
      {
        label: 'Settings',
        items: [
          {
            label: 'Highlights',
            icon: <RiStarLine />,
            to: w.highlights(...p, portalId),
            active: at('resources', 'highlights')
          },
          {
            label: 'Settings',
            icon: <RiSettings3Line />,
            to: w.settings(...p, portalId),
            active: at('settings')
          }
        ]
      }
    ]
  };
};

// Developer (§6) -------------------------------------------------------------

export let developerSubNav = (scope: ShellScope, pathname: string): SubNavSpec => {
  let p = params(scope);
  let segments = getProductSegments(pathname);
  let pon = (...prefixes: string[][]) => productOn(pathname, ...prefixes);
  let networking = scope.flag('networking-enabled') === true;

  let configTabs = ['configs', 'auth-configs', 'config', 'auth-config', 'config-vaults'];
  let onDeployments =
    segments[0] == 'configurations' && !configTabs.includes(segments[1] ?? '');

  let groups: SubNavGroup[] = [
    {
      items: [
        {
          label: 'Overview',
          icon: <RiDashboardLine />,
          to: Paths.instance.developer(...p),
          active: segments[0] == 'developer' && segments.length == 1
        }
      ]
    },
    {
      label: 'Providers',
      items: [
        {
          label: 'Deployments',
          icon: <RiRocketLine />,
          to: Paths.instance.providerDeployments(...p),
          active: onDeployments
        },
        {
          label: 'Provider Configs',
          icon: <RiEqualizerLine />,
          to: Paths.instance.providerConfigs(...p),
          active: pon(['configurations', 'configs'], ['configurations', 'config'])
        },
        {
          label: 'Auth Configs',
          icon: <RiLockLine />,
          to: Paths.instance.providerAuthConfigs(...p),
          active: pon(['configurations', 'auth-configs'], ['configurations', 'auth-config'])
        },
        {
          // Lives in the settings area, like in the cloud.
          label: 'Auth Credentials',
          icon: <RiKey2Line />,
          to: Paths.organization.instance.providerAuthCredentials(...p),
          active: false
        }
      ]
    },
    ...(networking
      ? [
          {
            label: 'Networking',
            items: [
              {
                label: 'Firewalls',
                icon: <RiShieldLine />,
                to: Paths.instance.networkFirewalls(...p),
                active: pon(['network', 'firewalls'], ['network', 'firewall'])
              },
              {
                label: 'Internet Gateway',
                icon: <RiGlobalLine />,
                to: Paths.instance.networkSettings(...p),
                active: pon(['network', 'internet-gateway'])
              }
            ]
          }
        ]
      : []),
    {
      label: 'API Access',
      items: [
        {
          label: 'API Keys',
          icon: <RiKeyLine />,
          to: Paths.instance.developer(...p, 'api-keys'),
          active: pon(['developer', 'api-keys'])
        },
        {
          label: 'Client Tokens',
          icon: <RiTicket2Line />,
          to: Paths.instance.developerClientTokens(...p),
          active: pon(['developer', 'client-tokens'])
        },
        {
          label: 'Settings',
          icon: <RiSettings3Line />,
          to: Paths.instance.developer(...p, 'api'),
          active: pon(['developer', 'api'])
        }
      ]
    },
    {
      label: 'Activity',
      items: [
        {
          label: 'Audit Log',
          icon: <RiFileList3Line />,
          to: Paths.instance.developerAuditLogs(...p),
          active: pon(['developer', 'audit-logs'])
        }
      ]
    }
  ];

  return { key: 'developer', groups };
};

// Settings area (§7) ---------------------------------------------------------

export let settingsSubNav = (
  scope: ShellScope,
  pathname: string,
  header?: SubNavSpec['header']
): SubNavSpec => {
  let o = scope.organization;
  let p = params(scope);
  let org = Paths.organization;
  let ps = org.projectSettings;
  let inst = org.instance;

  let link = (
    label: string,
    to: string,
    icon: React.ReactNode,
    opts?: { prefix?: boolean }
  ): SubNavLink => ({
    label,
    icon,
    to,
    active: on(pathname, to, opts)
  });

  let parent = (label: string, icon: React.ReactNode, children: SubNavLink[]): SubNavLink => ({
    label,
    icon,
    to: children[0]!.to,
    active: children.some(c => c.active),
    children
  });

  return {
    key: 'settings',
    header,
    groups: [
      { items: [link('Home', org.settings(o), <RiHome6Line />)] },
      {
        label: 'Organization',
        items: [
          link('General', org.general(o), <RiBuilding2Line />),
          link('Notifications', org.notifications(o), <RiBellLine />),
          link('Usage', org.usage(o), <RiBarChartBoxLine />),
          parent('Team', <RiTeamLine />, [
            link('Members', org.members(o), <RiUserLine />, { prefix: true }),
            link('Invites', org.invites(o), <RiMailSendLine />),
            link('Teams', org.teams(o), <RiGroupLine />, { prefix: true })
          ]),
          parent('Access', <RiShieldKeyholeLine />, [
            link('Policies', org.policies(o), <RiFileShieldLine />, { prefix: true }),
            link('Roles', org.roles(o), <RiUserSettingsLine />, { prefix: true }),
            link('OAuth Apps', org.oauthApps(o), <RiAppsLine />, { prefix: true }),
            link('Service Accounts', org.serviceAccounts(o), <RiRobotLine />, { prefix: true }),
            link('Authorized Apps', org.authorizedApplications(o), <RiCheckboxCircleLine />, { prefix: true }),
            link('CLI Devices', org.cliDevices(o), <RiTerminalLine />)
          ]),
          link('Projects', org.projects(o), <RiFolder3Line />)
        ]
      },
      {
        label: 'Project',
        items: [
          parent('General', <RiSettings3Line />, [
            {
              label: 'Profile',
              icon: <RiProfileLine />,
              to: inst(...p),
              active: on(pathname, inst(...p)) || on(pathname, org.project(o, scope.project))
            },
            link('Branding', ps.branding(o, scope.project), <RiPaletteLine />)
          ]),
          parent('Security', <RiShieldLine />, [link('Log Retention', ps.retention(o, scope.project), <RiTimeLine />)]),
          parent('Source Control', <RiGitBranchLine />, [
            link('Repositories', ps.scm(o, scope.project), <RiGitRepositoryLine />),
            link('Connections', ps.scmConnections(o, scope.project), <RiLinksLine />),
            link('Providers', ps.scmProviders(o, scope.project), <RiGithubLine />)
          ])
        ]
      },
      {
        label: 'Integrations and MCP',
        items: [
          parent('MCP Configuration', <RiServerLine />, [
            link('MCP Auth', ps.authConfig(o, scope.project), <RiLockLine />),
            link('Tool Calling', ps.toolCalling(o, scope.project), <RiToolsLine />),
            link('Sessionless MCP', ps.sessionlessMcp(o, scope.project), <RiFlashlightLine />)
          ]),
          parent('Provider Auth', <RiKey2Line />, [
            {
              label: 'Auth Credentials',
              icon: <RiKeyLine />,
              to: inst.providerAuthCredentials(...p),
              active:
                on(pathname, inst.providerAuthCredentials(...p)) ||
                on(pathname, inst(...p, 'configurations', 'auth-credential'), { prefix: true })
            }
          ]),
          parent('Identity', <RiFingerprintLine />, [
            {
              label: 'Delegation Configs',
              icon: <RiFileSettingsLine />,
              to: inst.identity.delegationConfigs(...p),
              active:
                on(pathname, inst.identity.delegationConfigs(...p), { prefix: true }) ||
                on(pathname, inst(...p, 'identity', 'delegation-config'), { prefix: true })
            },
            {
              label: 'Delegations',
              icon: <RiShareForwardLine />,
              to: inst.identity.delegations(...p),
              active:
                on(pathname, inst.identity.delegations(...p), { prefix: true }) ||
                on(pathname, inst(...p, 'identity', 'delegation'), { prefix: true })
            },
            link('Default Config', ps.delegationConfig(o, scope.project), <RiSettings2Line />)
          ]),
          parent('Prompt Monitoring', <RiEyeLine />, [
            {
              label: 'Alerts',
              icon: <RiAlarmWarningLine />,
              to: inst.alerts(...p),
              active:
                on(pathname, inst.alerts(...p), { prefix: true }) ||
                on(pathname, inst(...p, 'alert'), { prefix: true })
            },
            {
              label: 'Monitors',
              icon: <RiRadarLine />,
              to: inst.monitors(...p),
              active:
                on(pathname, inst.monitors(...p), { prefix: true }) ||
                on(pathname, inst(...p, 'monitor'), { prefix: true })
            },
            link('ProtoGuard', inst.protoguard(...p), <RiShieldCheckLine />, { prefix: true })
          ])
        ]
      },
      {
        label: 'Workforce',
        items: [
          {
            label: 'Portals',
            icon: <RiLayoutGridLine />,
            to: inst.portals(...p),
            active: on(pathname, inst.portals(...p))
          },
          link('Settings', inst.portalsSettings(...p), <RiUserSettingsLine />)
        ]
      },
      {
        label: 'Skills',
        items: [link('General', inst.skillsGeneral(...p), <RiSparkling2Line />)]
      }
    ]
  };
};

// Account area (§8) ----------------------------------------------------------

export let accountSubNav = (pathname: string, header?: SubNavSpec['header']): SubNavSpec => ({
  key: 'account',
  header,
  groups: [
    {
      label: 'Account',
      items: [
        {
          label: 'Profile',
          icon: <RiUserLine />,
          to: Paths.account.profile(),
          active: on(pathname, Paths.account()) || on(pathname, Paths.account.profile())
        }
      ]
    },
    {
      label: 'Access',
      items: [
        {
          label: 'Portals',
          icon: <RiLayoutGridLine />,
          to: Paths.account.portals(),
          active: on(pathname, Paths.account.portals())
        }
      ]
    }
  ]
});

/** Every page link of a sub-nav, flattened (for the ⌘K palette). */
export let flattenSubNav = (spec: SubNavSpec | null) =>
  (spec?.groups ?? []).flatMap(group =>
    group.items.flatMap(item => [
      // A parent that only opens its first child is not a page of its own.
      ...(item.children?.some(c => c.to == item.to)
        ? []
        : [{ group: group.label, link: item }]),
      ...(item.children ?? []).map(child => ({
        group: group.label ? `${group.label} › ${String(item.label)}` : String(item.label),
        link: child
      }))
    ])
  );
